import { useState, useMemo } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Search, RotateCcw } from 'lucide-react'
import { useLocationContext } from '@/components/layout/LocationLayout'
import {
  useLocationCatalog,
  useLocationCatalogItems,
  usePricingMutations,
} from '@/hooks/usePricing'
import {
  SERVICE_TYPE_LABELS,
  UNIT_OF_MEASURE_LABELS,
  type ServiceType,
  type ResolvedCatalogItem,
} from '@/types/pricing'
import { useCatalogGroups } from '@/hooks/usePricing'

const SERVICE_TYPE_FILTERS: { value: ServiceType | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'fuel', label: 'Fuel' },
  { value: 'ramp_fee', label: 'Ramp' },
  { value: 'parking', label: 'Parking' },
  { value: 'hangar_nightly', label: 'Hangar' },
  { value: 'service', label: 'Services' },
]

export function LocationCatalogTab() {
  const { locationId } = useLocationContext()
  const { data: resolvedCatalog, isLoading: catalogLoading } = useLocationCatalog(locationId)
  const { data: rawItems, isLoading: itemsLoading } = useLocationCatalogItems(locationId)
  const { data: groups } = useCatalogGroups()
  const mutations = usePricingMutations()
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<ServiceType | 'all'>('all')
  const [groupFilter, setGroupFilter] = useState<string | 'all'>('all')

  // Build map of raw toggle state: catalogItemId -> row
  const toggleMap = useMemo(
    () => new Map(rawItems?.map((item) => [item.catalog_item_id, item]) ?? []),
    [rawItems]
  )

  // We show the resolved catalog (which already filters out org-deactivated items)
  // plus any items that have been toggled off at location level (so user can re-enable)
  const allItems = useMemo(() => {
    if (!resolvedCatalog) return []

    // Items toggled off won't appear in resolved catalog, but we need to show them
    // Get all org catalog items that are toggled off at this location
    const resolvedIds = new Set(resolvedCatalog.map((i) => i.catalogItemId))
    const disabledItems: ResolvedCatalogItem[] = []

    for (const [catalogItemId, toggle] of toggleMap) {
      if (!toggle.is_available && !resolvedIds.has(catalogItemId)) {
        // We need minimal info — we'll mark it as disabled
        disabledItems.push({
          catalogItemId,
          code: '—',
          displayName: `Disabled item (${catalogItemId.slice(0, 8)}...)`,
          description: null,
          type: 'service' as ServiceType,
          unitOfMeasure: 'per_service' as never,
          fuelType: null,
          glCode: null,
          revenueCategory: null,
          isSystemItem: false,
          isThirdParty: false,
          isInventoryItem: false,
          sortOrder: 999,
          groupId: null,
          groupCode: null,
          groupName: null,
          groupSortOrder: 999,
        })
      }
    }

    return [...resolvedCatalog, ...disabledItems]
  }, [resolvedCatalog, toggleMap])

  const filtered = useMemo(() => {
    const q = search.toLowerCase()
    return allItems.filter((item) => {
      if (typeFilter !== 'all' && item.type !== typeFilter) return false
      if (groupFilter !== 'all' && item.groupCode !== groupFilter) return false
      return (
        item.displayName.toLowerCase().includes(q) ||
        item.code.toLowerCase().includes(q)
      )
    })
  }, [allItems, search, typeFilter, groupFilter])

  // Count by type
  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = { all: allItems.length }
    for (const item of allItems) {
      counts[item.type] = (counts[item.type] ?? 0) + 1
    }
    return counts
  }, [allItems])

  // Groups available in the "service" type for sub-filtering
  const serviceGroups = useMemo(() => {
    if (!groups) return []
    return groups.filter(
      (g) => g.allowedTypes?.includes('service') || g.allowedTypes?.includes('oil') || g.allowedTypes?.includes('concession')
    )
  }, [groups])

  const isLoading = catalogLoading || itemsLoading

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-72 mt-2" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  const handleToggle = (item: ResolvedCatalogItem, checked: boolean) => {
    mutations.toggleCatalogAvailability.mutate({
      locationId,
      catalogItemId: item.catalogItemId,
      isAvailable: checked,
    })
  }

  const handleReset = (catalogItemId: string) => {
    const toggle = toggleMap.get(catalogItemId)
    if (!toggle) return
    mutations.resetCatalogItem.mutate({ id: toggle.id, locationId })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Location Services</CardTitle>
        <CardDescription>
          Toggle which services are available at this location. Items default to available
          unless explicitly disabled.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search services..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-1">
            {SERVICE_TYPE_FILTERS.map(({ value, label }) => (
              <Button
                key={value}
                variant={typeFilter === value ? 'default' : 'outline'}
                size="sm"
                className="h-7 text-xs"
                onClick={() => { setTypeFilter(value); setGroupFilter('all') }}
              >
                {label}
                {typeCounts[value] !== undefined && (
                  <Badge variant="secondary" className="ml-1 text-[10px] px-1 h-4">
                    {typeCounts[value]}
                  </Badge>
                )}
              </Button>
            ))}
          </div>
          {typeFilter === 'service' && serviceGroups.length > 0 && (
            <div className="flex items-center gap-1">
              <Button
                variant={groupFilter === 'all' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-6 text-[11px]"
                onClick={() => setGroupFilter('all')}
              >
                All Groups
              </Button>
              {serviceGroups.map((g) => (
                <Button
                  key={g.code}
                  variant={groupFilter === g.code ? 'secondary' : 'ghost'}
                  size="sm"
                  className="h-6 text-[11px]"
                  onClick={() => setGroupFilter(g.code)}
                >
                  {g.name}
                </Button>
              ))}
            </div>
          )}
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Group</TableHead>
              <TableHead>Unit</TableHead>
              <TableHead className="w-[100px]">Available</TableHead>
              <TableHead className="w-[70px]">Reset</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((item) => {
              const toggle = toggleMap.get(item.catalogItemId)
              const isAvailable = toggle ? toggle.is_available : true
              const hasOverride = !!toggle

              return (
                <TableRow
                  key={item.catalogItemId}
                  className={!isAvailable ? 'opacity-50' : undefined}
                >
                  <TableCell className="font-mono text-xs">{item.code}</TableCell>
                  <TableCell>{item.displayName}</TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="text-xs">
                      {SERVICE_TYPE_LABELS[item.type] ?? item.type}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {item.groupName ?? <span className="text-xs text-muted-foreground/60">—</span>}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {UNIT_OF_MEASURE_LABELS[item.unitOfMeasure] ?? item.unitOfMeasure}
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={isAvailable}
                      onCheckedChange={(checked) => handleToggle(item, checked)}
                      disabled={mutations.toggleCatalogAvailability.isPending}
                    />
                  </TableCell>
                  <TableCell>
                    {hasOverride && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => handleReset(item.catalogItemId)}
                        title="Reset to default"
                        disabled={mutations.resetCatalogItem.isPending}
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                  No services found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}
